import type { TranscriptSegment, TranscriptChunk, TranscriptTimestamp } from './normalize';

const DEFAULT_MAX_WORDS = 30;

const normalizeTimestamp = (timestamp?: TranscriptTimestamp): TranscriptTimestamp => {
  if (
    Array.isArray(timestamp) &&
    timestamp.length === 2 &&
    Number.isFinite(timestamp[0]) &&
    Number.isFinite(timestamp[1])
  ) {
    return [Number(timestamp[0]), Number(timestamp[1])];
  }
  return [0, 0];
};

const normalizeChunkList = (segment: TranscriptSegment): TranscriptChunk[] => {
  if (Array.isArray(segment?.chunks) && segment.chunks.length > 0) {
    return segment.chunks.map((chunk) => ({
      text: typeof chunk?.text === 'string' ? chunk.text : '',
      translation:
        (typeof chunk?.translation === 'string' && chunk.translation.length > 0) || typeof chunk?.translation === 'object'
          ? chunk.translation
          : undefined,
      timestamp: normalizeTimestamp(chunk?.timestamp ?? segment?.timestamp),
    }));
  }

  const fallbackText = typeof segment?.text === 'string' ? segment.text : '';
  return [
    {
      text: fallbackText,
      translation:
        (typeof segment?.translation === 'string' && segment.translation.length > 0) || typeof segment?.translation === 'object'
          ? segment.translation
          : undefined,
      timestamp: normalizeTimestamp(segment?.timestamp),
    },
  ];
};

const countWords = (text: string): number => {
  return text
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0).length;
};

export interface MergeOptions {
  maxWordsPerSegment?: number;
  /** When true, skip merging and return normalized segments as-is */
  disableMerge?: boolean;
}

export const mergeTranscriptSegments = (
  segments: TranscriptSegment[],
  options?: MergeOptions,
): TranscriptSegment[] => {
  if (!Array.isArray(segments) || segments.length === 0) {
    return [];
  }

  // 🔥 如果禁用合并，直接返回规范化后的 segments
  if (options?.disableMerge) {
    return segments
      .filter((segment) => typeof segment?.text === 'string' && segment.text.trim().length > 0)
      .map((segment) => ({
        text: segment.text.trim(),
        translation: segment.translation,
        timestamp: normalizeTimestamp(segment.timestamp),
        chunks: normalizeChunkList(segment),
        sentences: segment.sentences,
      }));
  }

  const maxWords = Math.max(1, options?.maxWordsPerSegment ?? DEFAULT_MAX_WORDS);
  const merged: TranscriptSegment[] = [];

  let current: TranscriptSegment | null = null;
  let currentWordCount = 0;

  const pushCurrent = () => {
    if (current && typeof current.text === 'string' && current.text.trim().length > 0) {
      merged.push({
        text: current.text,
        translation: current.translation,
        timestamp: [...current.timestamp],
        chunks: current.chunks.map((chunk) => ({
          text: chunk.text,
          translation: chunk.translation,
          timestamp: [...chunk.timestamp],
        })),
        sentences: current.sentences,
      });
    }
    current = null;
    currentWordCount = 0;
  };

  for (const segment of segments) {
    const segmentText = typeof segment?.text === 'string' ? segment.text.trim() : '';
    if (!segmentText) {
      continue;
    }

    const timestamp = normalizeTimestamp(segment.timestamp);
    const wordCount = countWords(segmentText);
    const chunkList = normalizeChunkList(segment);

    const createNewCurrent = (): void => {
      current = {
        text: segmentText,
        translation: segment.translation,
        timestamp: [...timestamp],
        chunks: [...chunkList],
        sentences: segment.sentences,
      } as TranscriptSegment;
      currentWordCount = wordCount;
    };

    if (!current) {
      createNewCurrent();
      continue;
    }

    if (currentWordCount + wordCount > maxWords) {
      pushCurrent();
      createNewCurrent();
      continue;
    }

    // At this point, current is guaranteed to be non-null due to earlier checks
    // Use non-null assertion to help TypeScript understand the control flow
    if (!current) {
      createNewCurrent();
      continue;
    }

    // TypeScript assertion: current is non-null here
    // Use a local variable to help TypeScript understand the type
    const currentSegment: TranscriptSegment = current;
    currentSegment.text = `${currentSegment.text} ${segmentText}`.trim();
    currentSegment.timestamp[1] = timestamp[1];
    
    // Merge translations when merging segments
    // If both are objects, merge them. If strings, concat.
    const currentTrans = currentSegment.translation;
    const segmentTrans = segment.translation;
    
    if (typeof currentTrans === 'object' && typeof segmentTrans === 'object') {
       // Merge object keys: if same key, concat strings
       const mergedTrans: Record<string, string> = { ...currentTrans };
       for (const key in segmentTrans) {
           if (mergedTrans[key]) {
               mergedTrans[key] = `${mergedTrans[key]} ${segmentTrans[key]}`; // Add space for flow
           } else {
               mergedTrans[key] = segmentTrans[key];
           }
       }
       currentSegment.translation = mergedTrans;
    } else if (typeof currentTrans === 'string' && typeof segmentTrans === 'string') {
       currentSegment.translation = `${currentTrans} ${segmentTrans}`;
    } else {
       // Mixed or missing translations
       if (!currentTrans && segmentTrans) {
           // If current has no translation, adopt segment's translation
           if (typeof segmentTrans === 'object') {
               currentSegment.translation = { ...segmentTrans };
           } else {
               currentSegment.translation = segmentTrans;
           }
       } else if (currentTrans && segmentTrans) {
           // Both exist but mismatched types (one string, one object)
           
           // Case 1: Current is string, Segment is object
           if (typeof currentTrans === 'string' && typeof segmentTrans === 'object') {
               const newCurrent: Record<string, string> = { 'zh': currentTrans };
               // Merge segmentTrans into it
               for (const key in segmentTrans) {
                   if (newCurrent[key]) {
                       newCurrent[key] = `${newCurrent[key]} ${segmentTrans[key]}`;
                   } else {
                       newCurrent[key] = segmentTrans[key];
                   }
               }
               currentSegment.translation = newCurrent;
           }
           // Case 2: Current is object, Segment is string
           else if (typeof currentTrans === 'object' && typeof segmentTrans === 'string') {
               // Merge string into 'zh' key of current
               const newCurrent = { ...currentTrans };
               newCurrent['zh'] = newCurrent['zh'] ? `${newCurrent['zh']} ${segmentTrans}` : segmentTrans;
               currentSegment.translation = newCurrent;
           }
       }
    }

    currentSegment.chunks.push(...chunkList);
    currentWordCount += wordCount;
  }

  pushCurrent();

  return merged;
};
